// src/pages/admin/AdminDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { Row } from "react-bootstrap";
import {
  FiUsers,
  FiBriefcase,
  FiCheckCircle,
  FiCalendar,
  FiDollarSign,
  FiActivity,
  FiEye,
} from "react-icons/fi";

import {
  getDashboardStats,
  getAdminProjects,
  getAdminRoles,
  getAdminAuditions,
  getPaymentLogs
} from "../../services/adminApi";

import DashboardHeader from "./dashboard/components/DashboardHeader";
import DashboardKpiCards from "./dashboard/components/DashboardKpiCards";
import DashboardGrowthCharts from "./dashboard/components/DashboardGrowthCharts";
import DashboardActivityCharts from "./dashboard/components/DashboardActivityCharts";
import RecentProjectsTable from "./dashboard/components/RecentProjectsTable";
import RecentAuditionsList from "./dashboard/components/RecentAuditionsList";

import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [monthlyData, setMonthlyData] = useState([]);
  const [roleCategoryData, setRoleCategoryData] = useState([]);
  const [auditionData, setAuditionData] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [auditions, setAuditions] = useState([]);
  const [loading, setLoading] = useState(true);

  const COLORS = [
    "#8B0000",
    "#A30000",
    "#C1121F",
    "#DC143C",
    "#F08080"
  ];

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const [
        statsRes,
        projectsRes,
        rolesRes,
        auditionsRes,
        paymentsRes
      ] = await Promise.all([
        getDashboardStats().catch(() => ({ data: {} })),
        getAdminProjects().catch(() => ({ data: [] })),
        getAdminRoles().catch(() => ({ data: [] })),
        getAdminAuditions().catch(() => ({ data: {} })),
        getPaymentLogs().catch(() => ({ data: { data: [] } }))
      ]);

      const statsData = statsRes?.data || {};
      const roles = rolesRes?.data || [];
      const paymentRecords = paymentsRes?.data?.data || (Array.isArray(paymentsRes?.data) ? paymentsRes.data : []);
      
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthMap = {};

      monthNames.forEach((m) => {
        monthMap[m] = { month: m, revenue: 0, users: 0 };
      });

      roles.forEach((role) => {
        if (role.created_at) {
          const month = new Date(role.created_at).toLocaleString("default", { month: "short" });
          if (monthMap[month]) {
            monthMap[month].users += 1;
          }
        }
      });

      let totalPaymentRevenue = 0;

      if (paymentRecords.length > 0) {
        paymentRecords.forEach((p) => {
          const status = String(p.payment_status || p.status || p.booking_status || '').toUpperCase();
          // Exclude refunded, cancelled, or failed transactions from revenue
          if (status === 'REFUNDED' || status === 'CANCELLED' || status === 'FAILED') {
            return;
          }

          const amt = Number(p.amount || p.total_amount || p.paid_amount || 0);
          totalPaymentRevenue += amt;
          const pDate = p.created_at || p.payment_date || p.createdAt;
          if (pDate) {
            const month = new Date(pDate).toLocaleString("default", { month: "short" });
            if (monthMap[month]) {
              monthMap[month].revenue += amt;
            }
          }
        });
      }

      const finalRevenue = statsData.revenue !== undefined 
        ? Number(statsData.revenue) 
        : (statsData.total_revenue !== undefined ? Number(statsData.total_revenue) : totalPaymentRevenue);

      setStats({
        candidates: statsData.candidates || 0,
        castingDirectors: statsData.castingDirectors || 0,
        projects: statsData.projects || 0,
        roles: rolesRes?.data?.length || 0,
        auditions: auditionsRes?.data?.total || 0,
        revenue: finalRevenue,
        activeUsers: statsData.candidates || 0,
        applications: auditionsRes?.data?.total || 0,
      });

      setMonthlyData(Object.values(monthMap));

      const projectsList = projectsRes?.data?.data || (Array.isArray(projectsRes?.data) ? projectsRes.data : []);
      setRecentProjects(projectsList.slice(0, 5));

      const categoryMap = {};
      projectsList.forEach((p) => {
        const type = p.project_type || "Film/TV";
        categoryMap[type] = (categoryMap[type] || 0) + 1;
      });

      const formattedCategoryData = Object.keys(categoryMap).map((key) => ({
        name: key,
        value: categoryMap[key],
      }));

      setRoleCategoryData(formattedCategoryData.length > 0 ? formattedCategoryData : [{ name: 'Films', value: 3 }, { name: 'Web Series', value: 2 }]);

      const auditionRows = auditionsRes?.data?.data || [];
      const weekMap = {};

      auditionRows.forEach((a) => {
        const day = a.auditionMonth || "N/A";
        weekMap[day] = (weekMap[day] || 0) + 1;
      });

      setAuditionData(
        Object.keys(weekMap).map((key) => ({
          name: key,
          auditions: weekMap[key],
        }))
      );

      setAuditions(auditionRows.slice(0, 5));
    } catch (err) {
      console.log("Dashboard Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = useMemo(
    () => [
      {
        title: "Candidates",
        value: stats.candidates || 0,
        icon: <FiUsers />,
        growth: "+12%",
      },
      {
        title: "Casting Directors",
        value: stats.castingDirectors || 0,
        icon: <FiUsers />,
        growth: "+5%",
      },
      {
        title: "Projects",
        value: stats.projects || 0,
        icon: <FiBriefcase />,
        growth: "+18%",
      },
      {
        title: "Roles",
        value: stats.roles || 0,
        icon: <FiCheckCircle />,
        growth: "+9%",
      },
      {
        title: "Auditions",
        value: stats.auditions || 0,
        icon: <FiCalendar />,
        growth: "+23%",
      },
      {
        title: "Revenue",
        value: `₹${Number(stats.revenue || 0).toLocaleString()}`,
        icon: <FiDollarSign />,
        growth: "+31%",
      },
      {
        title: "Applications",
        value: stats.applications || 0,
        icon: <FiActivity />,
        growth: "+14%",
      },
      {
        title: "Active Users",
        value: stats.activeUsers || 0,
        icon: <FiEye />,
        growth: "+7%",
      },
    ],
    [stats]
  );

  return (
    <div className="admin-dashboard-page">
      <DashboardHeader />
      <DashboardKpiCards statCards={statCards} />
      <DashboardGrowthCharts 
        monthlyData={monthlyData} 
        roleCategoryData={roleCategoryData} 
        COLORS={COLORS} 
      />
      <DashboardActivityCharts 
        auditionData={auditionData} 
        monthlyData={monthlyData} 
      />
      <Row className="g-4">
        <RecentProjectsTable recentProjects={recentProjects} />
        <RecentAuditionsList auditions={auditions} />
      </Row>
    </div>
  );
};

export default AdminDashboard;